import { useCallback, useRef, useState } from "react";
import { useCreateSourcingEventMutation } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useApiError } from "../errors/useApiError";
import { departmentsForNewEvent } from "./eventCreationDepartments";

export const useCreateSourcingEvent = (onCreated: (eventId: string) => void) => {
  const authState = useLoggedInWithOrgContextState();
  const [create, { isLoading }] = useCreateSourcingEventMutation();
  const { trigger } = useApiError();
  const [isChoosingDepartment, setIsChoosingDepartment] = useState(false);
  const requestInFlight = useRef(false);

  const departments = departmentsForNewEvent(authState.selectedOrg);

  const createWith = useCallback(
    async (homeDepartmentId?: string) => {
      if (requestInFlight.current) return;
      requestInFlight.current = true;
      try {
        const event = await create({ createSourcingEventV2Request: { title: "", homeDepartmentId } }).unwrap();
        setIsChoosingDepartment(false);
        onCreated(event.id);
      } catch (e) {
        trigger(e);
      } finally {
        requestInFlight.current = false;
      }
    },
    [create, onCreated, trigger]
  );

  const start = useCallback(() => {
    if (requestInFlight.current) return;
    if (departments.length === 1) return void createWith(departments[0].id);
    setIsChoosingDepartment(true);
  }, [createWith, departments]);

  return {
    start,
    isCreating: isLoading,
    isChoosingDepartment,
    departments,
    cancelChoosingDepartment: () => setIsChoosingDepartment(false),
    confirmDepartment: (departmentId: string) => void createWith(departmentId),
  };
};
